import { ref, onScopeDispose } from 'vue';
import type { Ref } from 'vue';
import useApi from '@/helpers/api/api';

export interface ThumbnailItem {
  name: string;
  url: string;
}

interface BulkPreviewResult {
  assetId: number;
  name: string;
  resourceUid: string | null;
  previewUri: string | null;
  sourceUri: string | null;
  sourceState: number;
}

const SOURCE_STATE_DONE = 3;
const BULK_INPUT_CAP = 500;
const TRANSCODE_POLL_INTERVAL_MS = 5000;
const TRANSCODE_POLL_MAX_ATTEMPTS = 30; // ~2.5 min, then give up
const IMAGE_EXTS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'];

function getBaseName(input: string): string {
  if (!input) return '';
  const parts = input.split(/[\\/]/g);
  return parts[parts.length - 1];
}

function stripExtension(input: string): string {
  if (!input) return '';
  const idx = input.lastIndexOf('.');
  return idx > 0 ? input.substring(0, idx) : input;
}

function extractUuidFromUrl(url: string): string | null {
  if (!url) return null;
  try {
    const withoutQuery = url.split('?')[0] || url;
    const parts = withoutQuery.split('/');
    const last = parts[parts.length - 1] || '';
    const base = stripExtension(last);
    const guidRegex =
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
    return guidRegex.test(base) ? base : null;
  } catch (_) {
    return null;
  }
}

function isImageUrl(url: string): boolean {
  const ext = (url.split('?')[0].split('.').pop() || '').toLowerCase();
  return IMAGE_EXTS.includes(ext);
}

function chunk<T>(arr: T[], size: number): T[][] {
  if (arr.length === 0) return [];
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}

// ---------------------------------------------------------------------------
// Singleton state (module-scoped — shared by every component that calls
// useAssetThumbnails). See CODE_REVIEW.md §16.1.
// ---------------------------------------------------------------------------

const thumbMap: Ref<Record<string, string>> = ref({});
const failed = new Set<string>();
const inFlight = new Set<string>();

// Names currently waiting for transcode to finish. Value carries enough info
// to drive the per-row AssetLinks poll.
interface TranscodeEntry {
  item: ThumbnailItem;
  assetId: number;
  attempts: number;
}
const pendingTranscode = new Map<string, TranscodeEntry>();
let transcodePollTimer: number | null = null;

// Per-scope retention: each call to useAssetThumbnails() registers a Set of
// names it cares about. A name is pruned only when no scope retains it.
const scopes = new Map<symbol, Set<string>>();

function collectRetainedNames(): Set<string> {
  const all = new Set<string>();
  for (const set of scopes.values()) {
    for (const name of set) all.add(name);
  }
  return all;
}

function pruneToRetained() {
  const keep = collectRetainedNames();
  // Empty keep == nobody cares right now → clear everything resolved
  // (avoids unbounded growth across SPA navigations).
  const currentKeys = Object.keys(thumbMap.value);
  const needsPrune = currentKeys.some((k) => !keep.has(k));
  if (needsPrune) {
    const next: Record<string, string> = {};
    for (const k of currentKeys) if (keep.has(k)) next[k] = thumbMap.value[k];
    thumbMap.value = next;
  }
  for (const name of Array.from(failed)) {
    if (!keep.has(name)) failed.delete(name);
  }
  for (const name of Array.from(pendingTranscode.keys())) {
    if (!keep.has(name)) pendingTranscode.delete(name);
  }
  stopTranscodePollIfIdle();
}

// ---------------------------------------------------------------------------
// API calls
// ---------------------------------------------------------------------------

async function callBulkPreview(
  names: string[],
  resourceUids: string[],
): Promise<BulkPreviewResult[]> {
  if (names.length === 0 && resourceUids.length === 0) return [];
  const { post } = useApi();
  const { responseBody } = await post(
    'api/assets/BulkPreview',
    { names, resourceUids },
    { isLogoutOnInvalidAuth: true },
  );
  return Array.isArray(responseBody) ? (responseBody as BulkPreviewResult[]) : [];
}

async function fetchAssetLinksPreview(
  assetId: number | string,
): Promise<string | null> {
  const { post } = useApi();
  const result = await post(
    `api/assets/${assetId}/AssetLinks`,
    {},
    { isLogoutOnInvalidAuth: true },
  );
  return (
    result?.responseBody?.previewUrl || result?.responseBody?.previewUri || null
  );
}

// ---------------------------------------------------------------------------
// Lookup / join
// ---------------------------------------------------------------------------

interface ResultLookup {
  byUid: Map<string, BulkPreviewResult>;
  byName: Map<string, BulkPreviewResult>;
}

function buildLookup(results: BulkPreviewResult[]): ResultLookup {
  const byUid = new Map<string, BulkPreviewResult>();
  const byName = new Map<string, BulkPreviewResult>();
  for (const r of results) {
    if (r.resourceUid) byUid.set(r.resourceUid.toLowerCase(), r);
    if (r.name) byName.set(r.name, r);
  }
  return { byUid, byName };
}

function findResult(item: ThumbnailItem, lookup: ResultLookup): BulkPreviewResult | null {
  const uid = extractUuidFromUrl(item.url);
  if (uid) {
    const hit = lookup.byUid.get(uid.toLowerCase());
    if (hit) return hit;
  }
  if (item.name && lookup.byName.has(item.name)) {
    return lookup.byName.get(item.name)!;
  }
  const noExt = stripExtension(getBaseName(item.name));
  if (noExt && noExt !== item.name && lookup.byName.has(noExt)) {
    return lookup.byName.get(noExt)!;
  }
  return null;
}

function collectInputs(items: ThumbnailItem[]): { names: string[]; resourceUids: string[] } {
  const names = new Set<string>();
  const uids = new Set<string>();
  for (const item of items) {
    if (item.name) {
      names.add(item.name);
      const noExt = stripExtension(getBaseName(item.name));
      if (noExt && noExt !== item.name) names.add(noExt);
    }
    const uid = extractUuidFromUrl(item.url);
    if (uid) uids.add(uid);
  }
  return { names: Array.from(names), resourceUids: Array.from(uids) };
}

// ---------------------------------------------------------------------------
// Apply: take a bulk result for one row and update cache / pending state
// ---------------------------------------------------------------------------

function applyResult(
  item: ThumbnailItem,
  result: BulkPreviewResult | null,
  updates: Record<string, string>,
): void {
  if (!result) {
    failed.add(item.name);
    return;
  }
  if (result.previewUri) {
    updates[item.name] = result.previewUri;
    return;
  }
  if (result.sourceState < SOURCE_STATE_DONE) {
    pendingTranscode.set(item.name, { item, assetId: result.assetId, attempts: 0 });
    scheduleTranscodePoll();
    return;
  }
  // Transcode finished but no preview — fall back to source if it's an image.
  if (result.sourceUri && isImageUrl(result.sourceUri)) {
    updates[item.name] = result.sourceUri;
  } else {
    failed.add(item.name);
  }
}

// ---------------------------------------------------------------------------
// Transcode polling (per-row AssetLinks)
// ---------------------------------------------------------------------------

function scheduleTranscodePoll() {
  if (transcodePollTimer != null || pendingTranscode.size === 0) return;
  transcodePollTimer = window.setInterval(
    pollPendingTranscodes,
    TRANSCODE_POLL_INTERVAL_MS,
  );
}

function stopTranscodePollIfIdle() {
  if (pendingTranscode.size === 0 && transcodePollTimer != null) {
    window.clearInterval(transcodePollTimer);
    transcodePollTimer = null;
  }
}

async function pollPendingTranscodes() {
  if (pendingTranscode.size === 0) {
    stopTranscodePollIfIdle();
    return;
  }
  const snapshot = Array.from(pendingTranscode.entries());
  const updates: Record<string, string> = {};

  await Promise.all(
    snapshot.map(async ([name, entry]) => {
      entry.attempts += 1;
      try {
        const previewUri = await fetchAssetLinksPreview(entry.assetId);
        if (previewUri) {
          updates[name] = previewUri;
          pendingTranscode.delete(name);
        } else if (entry.attempts >= TRANSCODE_POLL_MAX_ATTEMPTS) {
          pendingTranscode.delete(name);
          failed.add(name);
        }
      } catch (e) {
        // transient — keep entry, try again on the next tick
        // (attempts still incremented; will eventually time out)
        console.error('[useAssetThumbnails] AssetLinks poll error for', name, e);
      }
    }),
  );

  if (Object.keys(updates).length) {
    thumbMap.value = { ...thumbMap.value, ...updates };
  }
  stopTranscodePollIfIdle();
}

// ---------------------------------------------------------------------------
// Public composable
// ---------------------------------------------------------------------------

export default function useAssetThumbnails() {
  const scopeId = Symbol('asset-thumbnails-scope');
  scopes.set(scopeId, new Set());

  async function resolveThumbnails(items: ThumbnailItem[]): Promise<void> {
    if (!items || items.length === 0) return;

    // Register the caller's interest so retain/prune doesn't drop them.
    const scopeNames = scopes.get(scopeId);
    if (scopeNames) {
      for (const item of items) {
        if (item.name) scopeNames.add(item.name);
      }
    }

    // Filter: only items we haven't already resolved, failed, queued, or
    // started in another worker.
    const fresh: ThumbnailItem[] = [];
    const seen = new Set<string>();
    for (const item of items) {
      if (!item.name || seen.has(item.name)) continue;
      seen.add(item.name);
      if (
        thumbMap.value[item.name] ||
        failed.has(item.name) ||
        inFlight.has(item.name) ||
        pendingTranscode.has(item.name)
      ) {
        continue;
      }
      inFlight.add(item.name);
      fresh.push(item);
    }
    if (fresh.length === 0) return;

    const { names, resourceUids } = collectInputs(fresh);
    const nameChunks = chunk(names, BULK_INPUT_CAP);
    const uidChunks = chunk(resourceUids, BULK_INPUT_CAP);
    const passes = Math.max(nameChunks.length, uidChunks.length, 1);

    try {
      const allResults: BulkPreviewResult[] = [];
      for (let i = 0; i < passes; i++) {
        const r = await callBulkPreview(
          nameChunks[i] ?? [],
          uidChunks[i] ?? [],
        );
        allResults.push(...r);
      }
      const lookup = buildLookup(allResults);

      // Batch updates so consumers only see one reactive flush instead of N.
      const updates: Record<string, string> = {};
      for (const item of fresh) {
        applyResult(item, findResult(item, lookup), updates);
      }
      if (Object.keys(updates).length) {
        thumbMap.value = { ...thumbMap.value, ...updates };
      }
    } catch (e) {
      // Transient (network / 5xx / 403). Do not mark items as failed —
      // a future resolveThumbnails call will retry them.
      console.error('[useAssetThumbnails] BulkPreview error:', e);
    } finally {
      for (const item of fresh) inFlight.delete(item.name);
    }
  }

  function retainThumbnails(names: string[]) {
    scopes.set(scopeId, new Set(names));
    pruneToRetained();
  }

  function clearFailedThumbnails() {
    failed.clear();
  }

  function resetThumbnails() {
    // Hard reset — affects all scopes. Use sparingly.
    inFlight.clear();
    failed.clear();
    pendingTranscode.clear();
    thumbMap.value = {};
    if (transcodePollTimer != null) {
      window.clearInterval(transcodePollTimer);
      transcodePollTimer = null;
    }
  }

  // Auto-clean when the component that called this composable unmounts.
  onScopeDispose(() => {
    scopes.delete(scopeId);
    pruneToRetained();
  });

  return {
    thumbMap,
    resolveThumbnails,
    resetThumbnails,
    retainThumbnails,
    clearFailedThumbnails,
  };
}
